import { CellSource, ColumnSource, GridSource, RowSource } from "@omegagrid/grid";
import { SheetDiff } from "@reporting/rp-types/analytics";
import { AxisItemProps } from "./tableAdapter";

/** Marker property of user-inserted rows/columns */
export const CUSTOM_PROP = 'custom';

/** Row keys persisted as overrides of server rows (values never — a data refresh must win) */
const ROW_OVERRIDE_KEYS = ['h', 's', 'z', 'hidden', 'collapsed', 'e', 't', 'f'] as const;
/** Column keys persisted as overrides of server columns */
const COLUMN_OVERRIDE_KEYS = ['w', 's', 'z', 'hidden', 'collapsed', 'e', 't', 'f'] as const;
/** Cell keys persisted as overrides on server cells (style, format, formula) */
const CELL_OVERRIDE_KEYS = ['s', 'z', 'f'] as const;
/** GridSource keys never copied verbatim into diff.model */
const MODEL_SKIP_KEYS = new Set(['rows', 'columns', 'cells', 'index', 'name', 'props', 'tab']);

export function createCustomName(): string {
	const uuid = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(16).slice(2);
	return '_u' + uuid.replace(/-/g, '').slice(0, 12);
}

function jsonEquals(a: unknown, b: unknown): boolean {
	if (a === b) return true;
	return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Drop function-valued and undefined fields so the value is safe to persist */
export function sanitize<T>(value: T): T {
	return value == null ? value : JSON.parse(JSON.stringify(value));
}

function elementName(el: RowSource | ColumnSource): string | undefined {
	return el.name as string | undefined;
}

function axisItemName(el: RowSource | ColumnSource): string | null {
	const props = el.props as AxisItemProps;
	if (!props?.items?.length) return null;
	const name = props.items.map(item => `${item.dimNum}:${item.id}`).join('|');
	const indicator = props.indicator;
	return indicator ? `${name}@${indicator.dataColumn ?? indicator.index}` : name;
}

function assignNames(elements: (RowSource | ColumnSource)[], prefix: string) {
	const used = new Set<string>();
	elements.forEach((el, i) => {
		let name = elementName(el) ?? axisItemName(el) ?? `${prefix}${i}`;
		if (used.has(name)) {
			let n = 2;
			while (used.has(`${name}#${n}`)) n++;
			name = `${name}#${n}`;
		}
		used.add(name);
		el.name = name;
	});
}

/**
 * Assign stable names to the rows and columns of a server-derived grid so user
 * modifications can be addressed by name and survive data changes. Dimension
 * rows/columns are named from their axis items (`dim:id|...@indicator`), the
 * rest (headers, text columns) positionally. Mutates and returns the source.
 */
export function assignGridNames(source: GridSource): GridSource {
	assignNames(source.rows ?? [], '_r');
	assignNames(source.columns ?? [], '_c');
	return source;
}

function isCustom(el: RowSource | ColumnSource, baseNames: Set<string>): boolean {
	const name = elementName(el);
	return !name || !baseNames.has(name) || !!(el.props as Record<string, unknown>)?.[CUSTOM_PROP];
}

function pickOverrides(
	current: RowSource | ColumnSource, base: RowSource | ColumnSource, keys: readonly string[]
): Record<string, unknown> | null {
	let overrides: Record<string, unknown> = null;
	for (const key of keys) {
		const value = (current as Record<string, unknown>)[key];
		if (typeof value == 'function') continue;
		if (!jsonEquals(value, (base as Record<string, unknown>)[key])) {
			(overrides ??= {})[key] = sanitize(value) ?? null;
		}
	}
	return overrides;
}

function diffElements(
	currentElements: (RowSource | ColumnSource)[],
	baseByName: Map<string, RowSource | ColumnSource>,
	overrideKeys: readonly string[],
	indexKey: 'r' | 'c',
) {
	const custom: Record<string, unknown>[] = [];
	const overrides: Record<string, Record<string, unknown>> = {};
	const baseNames = new Set(baseByName.keys());

	currentElements.forEach((el, i) => {
		if (isCustom(el, baseNames)) {
			el.name ??= createCustomName();
			el.props = {...el.props, [CUSTOM_PROP]: true};
			const source = sanitize({...el, data: undefined}) as Record<string, unknown>;
			delete source.data;
			source[indexKey] = i;
			// anchor to the immediately preceding element (custom anchors resolve because
			// diff order is document order), or to the following one for a first row/column
			const prev = currentElements[i - 1];
			const next = currentElements[i + 1];
			if (prev?.name) source.after = prev.name;
			else if (next?.name) source.before = next.name;
			custom.push(source);
		} else {
			const elementOverrides = pickOverrides(el, baseByName.get(el.name as string), overrideKeys);
			if (elementOverrides) overrides[el.name as string] = elementOverrides;
		}
	});

	return {custom, overrides};
}

function cellKey(rowName: string, colName: string): string {
	return rowName + ' ' + colName;
}

/**
 * Compute the persisted diff of an edited sheet against the pristine named
 * server grid. Custom rows/columns are detected by name absence in the base
 * (anchors are computed at diff time, so they stay correct after moves and
 * undo); on server elements only style/format/formula overrides are kept.
 * Returns null when the sheet holds no modifications.
 */
export function createSheetDiff(current: GridSource, base: GridSource): SheetDiff | null {
	const baseRows = new Map((base.rows ?? []).map(r => [r.name as string, r]));
	const baseColumns = new Map((base.columns ?? []).map(c => [c.name as string, c]));

	const rows = diffElements(current.rows ?? [], baseRows, ROW_OVERRIDE_KEYS, 'r');
	const columns = diffElements(current.columns ?? [], baseColumns, COLUMN_OVERRIDE_KEYS, 'c');

	const customRowNames = new Set(rows.custom.map(r => r.name as string));
	const customColumnNames = new Set(columns.custom.map(c => c.name as string));

	// cells addressed by (rowName, colName); base cells indexed the same way
	const baseCells = new Map<string, CellSource>();
	for (const cell of base.cells ?? []) {
		const rowName = base.rows?.[cell.r]?.name as string;
		const colName = base.columns?.[cell.c]?.name as string;
		if (rowName && colName) baseCells.set(cellKey(rowName, colName), cell);
	}

	const cells: SheetDiff['cells'] = {};
	let cellCount = 0;
	for (const cell of current.cells ?? []) {
		const rowName = current.rows?.[cell.r]?.name as string;
		const colName = current.columns?.[cell.c]?.name as string;
		if (!rowName || !colName) continue;

		let cellSource: Record<string, unknown>;
		if (customRowNames.has(rowName) || customColumnNames.has(colName)) {
			// cell on a user-inserted row/column — persist completely
			cellSource = sanitize({...cell, r: undefined, c: undefined});
			delete cellSource.r;
			delete cellSource.c;
		} else {
			// server cell — persist changed style/format/formula only, never the value
			cellSource = pickOverrides(cell, baseCells.get(cellKey(rowName, colName)) ?? {} as CellSource, CELL_OVERRIDE_KEYS);
		}

		if (cellSource && Object.keys(cellSource).length) {
			(cells[rowName] ??= {})[colName] = cellSource;
			cellCount++;
		}
	}

	// grid-model-level keys (styles, rules, options, names, ...) copied verbatim when changed
	const model: Record<string, unknown> = {};
	for (const key of Object.keys(current)) {
		if (MODEL_SKIP_KEYS.has(key)) continue;
		if (typeof current[key] == 'function') continue;
		if (!jsonEquals(current[key], base[key])) model[key] = sanitize(current[key]);
	}

	const diff: SheetDiff = {};
	if (rows.custom.length) diff.rows = rows.custom;
	if (columns.custom.length) diff.columns = columns.custom;
	if (cellCount) diff.cells = cells;
	if (Object.keys(rows.overrides).length) diff.row_overrides = rows.overrides;
	if (Object.keys(columns.overrides).length) diff.column_overrides = columns.overrides;
	if (Object.keys(model).length) diff.model = model;

	return Object.keys(diff).length ? diff : null;
}

/** Shallow-deep clone of a grid source that keeps function references (renderers) intact */
function cloneGridSource(source: GridSource): GridSource {
	return {
		...source,
		rows: source.rows?.map(r => ({...r, props: r.props ? {...r.props} : r.props})),
		columns: source.columns?.map(c => ({...c, props: c.props ? {...c.props} : c.props})),
		cells: source.cells?.map(c => ({...c})),
		options: source.options ? {...source.options} : source.options,
	};
}

function spliceElements(
	elements: (RowSource | ColumnSource)[],
	custom: Record<string, unknown>[] | undefined,
	indexKey: 'r' | 'c',
) {
	if (!custom?.length) return;
	const names = elements.map(el => el.name as string);

	for (const item of custom) {
		const {after, before, [indexKey]: absoluteIndex, ...source} = item;
		let index = -1;
		if (after != null) {
			const anchor = names.indexOf(after as string);
			if (anchor > -1) index = anchor + 1;
		}
		if (index < 0 && before != null) {
			index = names.indexOf(before as string);
		}
		if (index < 0 && typeof absoluteIndex == 'number') {
			index = Math.min(absoluteIndex, elements.length);
		}
		if (index < 0) index = elements.length;

		elements.splice(index, 0, source as RowSource | ColumnSource);
		names.splice(index, 0, source.name as string);
	}
}

function applyOverrides(
	elements: (RowSource | ColumnSource)[],
	overrides: Record<string, Record<string, unknown>> | undefined,
) {
	if (!overrides) return;
	const byName = new Map(elements.map(el => [el.name as string, el]));
	for (const name in overrides) {
		const el = byName.get(name);
		if (!el) continue;
		for (const key in overrides[name]) {
			const value = overrides[name][key];
			if (value === null) delete (el as Record<string, unknown>)[key];
			else (el as Record<string, unknown>)[key] = value;
		}
	}
}

/**
 * Merge a persisted sheet diff over a named server grid: splice custom columns
 * and rows (anchor `after` → index + 1, `before` → index, absolute index,
 * append), apply name-keyed element overrides and convert name-addressed cells
 * back to coordinates. Cells whose row/column no longer resolves are dropped.
 * Returns a new source; the base is not modified.
 */
export function applySheetDiff(base: GridSource, diff: SheetDiff | undefined | null): GridSource {
	const source = cloneGridSource(base);
	if (!diff) return source;

	if (diff.model) Object.assign(source, sanitize(diff.model));

	source.columns ??= [];
	source.rows ??= [];
	spliceElements(source.columns, diff.columns && sanitize(diff.columns), 'c');
	spliceElements(source.rows, diff.rows && sanitize(diff.rows), 'r');

	applyOverrides(source.rows, diff.row_overrides && sanitize(diff.row_overrides));
	applyOverrides(source.columns, diff.column_overrides && sanitize(diff.column_overrides));

	if (diff.cells) {
		const rowIndexes = new Map(source.rows.map((r, i) => [r.name as string, i]));
		const columnIndexes = new Map(source.columns.map((c, i) => [c.name as string, i]));
		const cellIndexes = new Map((source.cells ?? []).map((cell, i) => [cell.r + ':' + cell.c, i]));
		source.cells = [...(source.cells ?? [])];

		for (const rowName in diff.cells) {
			const r = rowIndexes.get(rowName);
			if (r == null) continue;
			for (const colName in diff.cells[rowName]) {
				const c = columnIndexes.get(colName);
				if (c == null) continue;
				const cellSource = {...sanitize(diff.cells[rowName][colName]), r, c} as CellSource;
				const existing = cellIndexes.get(r + ':' + c);
				if (existing != null) source.cells[existing] = {...source.cells[existing], ...cellSource};
				else source.cells.push(cellSource);
			}
		}
	}

	return source;
}
